import React, { useEffect } from 'react';

import { useSharedPluginState } from '@atlaskit/editor-common/hooks';
import type { PMPlugin, SelectionToolbarGroup } from '@atlaskit/editor-common/types';

import type { AIDefinitionsPlugin } from './aiDefinitionsPluginType';
import { onLivePageModeChange } from './pm-plugins/ai-definitions/commands';
import { createAIDefinitionsPMPlugin } from './pm-plugins/ai-definitions/plugin';
import { aiDefinitionsPluginKey } from './pm-plugins/ai-definitions/plugin-key';
import { getAIDefineButton } from './pm-plugins/selection-toolbar-buttons/define';
import { AIDefinitions } from './ui/AIDefinitions';
import { isLivePageViewMode } from './ui/utils/api';
import { selectionHasText } from './ui/utils/selection';

export const aiDefinitionsPlugin: AIDefinitionsPlugin = ({ api, config }) => {
	return {
		name: 'aiDefinitions',

		usePluginHook({ editorView }) {
			const { editorViewModeState } = useSharedPluginState(api, ['editorViewMode']);
			const livePageMode = editorViewModeState?.mode;

			useEffect(() => {
				onLivePageModeChange({ api, config, editorView, livePageMode });
			}, [editorView, livePageMode]);
		},

		pmPlugins() {
			const pmPlugins: PMPlugin[] = [];

			pmPlugins.push({
				name: 'aiDefinitions',
				plugin: (params) => createAIDefinitionsPMPlugin(params, { api, config }),
			});

			return pmPlugins;
		},

		getSharedState(editorState) {
			if (!editorState) {
				return null;
			}
			const pluginState = aiDefinitionsPluginKey.getState(editorState);

			if (!pluginState) {
				return null;
			}

			return {
				isReadingAidsPopupOpen: pluginState.isReadingAidsPopupOpen,
				readingAidsPopupKey: pluginState.readingAidsPopupKey,
				readingAidsPopupProps: pluginState.readingAidsPopupProps,
			};
		},

		contentComponent: ({ editorView }) => {
			return (
				<AIDefinitions
					api={api}
					editorView={editorView}
					contentId={config.contentId}
					ReadingAidsPopup={config.ReadingAidsPopup}
				/>
			);
		},

		pluginsOptions: {
			selectionToolbar: (state, intl) => {
				const items: SelectionToolbarGroup['items'] = [];

				if (api && isLivePageViewMode({ api }) && selectionHasText(state)) {
					items.push(getAIDefineButton({ intl, state, config, api }));
				}

				if (items.length) {
					return {
						rank: -10,
						isToolbarAbove: true,
						items,
					};
				}
			},
		},
	};
};
